import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import jwtDecode from 'jwt-decode'
import useAsyncStorageState from '@hooks/useAsyncStorageState'
import useAuth from '@hooks/useAuth'
import useSentry from '@hooks/useSentry'
import runAsync from '@lib/utils/runAsync'

interface CurrentUser {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  id?: string
  accessToken?: string
  refreshToken?: string
  jwtToken?: string
  jwtExpiration?: number
  companyId?: string
}

interface CurrentUserContextValue {
  currentUser: CurrentUser
  userAuthenticated: boolean
  userHasCompany: boolean
  setCurrentUser: Dispatch<SetStateAction<CurrentUser>>
  clearCurrentUser: () => void
  loadingCurrentUser: boolean
}

interface CurrentUserProviderProps {
  children: JSX.Element | JSX.Element[]
}

const REFRESH_LEAD_IN = 2000 // milliseconds

const CurrentUserContext = createContext<CurrentUserContextValue>({
  currentUser: {},
  userAuthenticated: false,
  userHasCompany: false,
  setCurrentUser: () => {},
  clearCurrentUser: () => {},
  loadingCurrentUser: true,
})

export const useCurrentUser = () => useContext(CurrentUserContext)

export const CurrentUserProvider = ({ children }: CurrentUserProviderProps) => {
  const [currentUser, setCurrentUser, loadingCurrentUser] = useAsyncStorageState<CurrentUser>('currentUser', {})
  const [firstSilentRefreshDone, setFirstSilentRefreshDone] = useState(false)
  const [jwtExpiration, setJwtExpiration] = useState<Maybe<number>>(undefined)

  const { getRefreshedJwt } = useAuth()
  const { captureException } = useSentry()

  /**
   * The refresh interval periodically sets a timeout function which runs the refreshing
   * a few seconds before expiration
   */
  const refreshInterval = useRef<Maybe<ReturnType<typeof setInterval>>>(undefined)
  const refreshTimeout = useRef<Maybe<ReturnType<typeof setTimeout>>>(undefined)

  const userAuthenticated = !!currentUser?.jwtToken
  const userHasCompany = !!currentUser?.companyId

  const clearCurrentUser = () => setCurrentUser({})

  const clearRefreshInterval = () => {
    if (!refreshInterval.current) return
    clearInterval(refreshInterval.current)
    refreshInterval.current = undefined
  }

  const clearRefreshTimeout = () => {
    if (!refreshTimeout.current) return
    clearTimeout(refreshTimeout.current)
    refreshTimeout.current = undefined
  }

  const clearRefresh = () => {
    clearRefreshInterval()
    clearRefreshTimeout()
  }

  // This has to be done with a thunk because getting the refreshToken
  // from the currentUser, inside of getRefreshedJwt results in a nasty
  // race condition, which gives currentUser.refreshToken `undefined`.
  const generateRefreshJwtCallback = (refreshToken: string) => async () => {
    try {
      const result = await getRefreshedJwt(refreshToken)
      if (result?.data.id_token && result?.data.refresh_token) {
        // Refresh token is also saved, because the Auth0 option
        // 'Refresh Token Rotation' is enabled.
        setCurrentUser({
          ...currentUser,
          jwtToken: result.data.id_token,
          refreshToken: result.data.refresh_token,
        })
      } else {
        // If the request fails for some reason, it means the current session
        // must be ended
        clearCurrentUser()
      }
    } catch (error: any) {
      captureException(new Error(`Silent authentication error: ${error.message}`))
      clearCurrentUser()
    }
  }

  const refreshJwt = () => {
    if (currentUser.refreshToken) {
      runAsync(generateRefreshJwtCallback(currentUser.refreshToken))
    }
  }

  const refreshJwtWithLeadInTime = () => {
    // This is necessary to refresh the jwt before it expires;
    // If this is not done, funny things may happen in the short time
    // that the jwt is expired and still hasn't been refreshed.
    refreshTimeout.current = setTimeout(() => {
      refreshJwt()
      clearRefreshTimeout()
    }, jwtExpiration! - REFRESH_LEAD_IN)
  }

  /**
   * Upon application start, try silent authentication
   * (getting a new JWT by using stored refreshToken)
   */
  useEffect(() => {
    if (firstSilentRefreshDone) return
    if (loadingCurrentUser) return

    refreshJwt()
    setFirstSilentRefreshDone(true)

    // Dependency array needs to contain `loadingCurrentUser` because, currentUser will
    // be undefined when the app first loads, since it's pulled from AsyncStorage
  }, [loadingCurrentUser, firstSilentRefreshDone])

  /**
   * Whenever the currentUser changes:
   * - Set jwt expiration whenever the jwtToken changes
   * - If the user isn't authenticated, then clear refresh interval and set jwt expiration to undefined
   */
  useEffect(() => {
    if (!userAuthenticated) {
      setJwtExpiration(undefined)
      clearRefresh()
      return
    }

    // Set jwt expiration
    const decodedToken: { exp: number, iat: number } = jwtDecode(currentUser.jwtToken as string)
    setJwtExpiration((decodedToken.exp - decodedToken.iat) * 1000) // milliseconds
  }, [currentUser])

  /**
   * Handle refresh in the background by periodically polling for new jwt(s)
   * A timeout is set if a `jwtExpiration` is stored in state.
   */
  useEffect(() => {
    clearRefresh()

    if (typeof jwtExpiration !== 'number') return

    // To avoid an OBOB, the first timeout must be called explicitly (used immediately)
    refreshJwtWithLeadInTime()
    refreshInterval.current = setInterval(() => {
      refreshJwtWithLeadInTime()
    }, jwtExpiration)
  }, [jwtExpiration])

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        userAuthenticated,
        userHasCompany,
        clearCurrentUser,
        setCurrentUser,
        loadingCurrentUser,
      }}
    >
      { children }
    </CurrentUserContext.Provider>
  )
}
